import { useCallback, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogTitle } from '@material-ui/core';
import { useWorkerStatus } from '../hooks/useWorkerStatus';
import type { PortalActivityWorkPath } from './contracts';
import { JourneyDialogContent } from './ActivityJourneyDialogParts';
import {
  useActivityWorkPathOptions,
  type ActivityWorkPathOption,
} from './useActivityWorkPathOptions';

export interface ActivityJourneyLauncherProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Confirmação da via: navega uma única vez, ou torna a falha visível. Nunca
 * silêncio (US-001.EC-3), e nunca duas navegações (US-001.EC-2).
 */
function useJourneyConfirmation(
  selectedOption: ActivityWorkPathOption | undefined,
  onClose: () => void,
) {
  const navigate = useNavigate();
  const [routeFailure, setRouteFailure] = useState(false);
  const navigatedRef = useRef(false);

  const confirm = useCallback(() => {
    if (navigatedRef.current) return;
    const path = selectedOption?.resolvePath();
    if (path === undefined) {
      // A condição também desabilita o botão; esta é a invariante que sobrevive
      // a uma regressão futura no desabilitar.
      setRouteFailure(true);
      return;
    }
    navigatedRef.current = true;
    onClose();
    navigate(path);
  }, [navigate, onClose, selectedOption]);

  return { confirm, routeFailure, clearFailure: () => setRouteFailure(false) };
}

/**
 * Seleção da via mais a decisão de confirmar. A via padrão é a primeira do
 * contrato, reavaliada a cada montagem — e o corpo só monta quando o diálogo
 * abre (ADR-003).
 */
function useJourneySelection(onClose: () => void) {
  const options = useActivityWorkPathOptions();
  const [selected, setSelected] = useState<PortalActivityWorkPath | undefined>(
    options[0]?.workPath,
  );
  const selectedOption = options.find(option => option.workPath === selected);
  const { confirm, routeFailure, clearFailure } = useJourneyConfirmation(
    selectedOption,
    onClose,
  );

  const select = useCallback(
    (workPath: PortalActivityWorkPath) => {
      clearFailure();
      setSelected(workPath);
    },
    [clearFailure],
  );

  return {
    options,
    selected,
    selectedOption,
    select,
    confirm,
    routeFailure,
    continueBlocked:
      selectedOption === undefined ||
      selectedOption.resolvePath() === undefined,
  };
}

const ActivityJourneyDialogBody = ({
  titleId,
  onClose,
}: {
  titleId: string;
  onClose: () => void;
}) => {
  const helperId = useId();
  const { presentation } = useWorkerStatus();
  const {
    options,
    selected,
    selectedOption,
    select,
    confirm,
    routeFailure,
    continueBlocked,
  } = useJourneySelection(onClose);

  return (
    <>
      <DialogTitle id={titleId}>Escolha a via da atividade</DialogTitle>
      <JourneyDialogContent
        presentation={presentation}
        routeFailure={routeFailure}
        options={options}
        selected={selected}
        onSelect={select}
        helperId={helperId}
        showHelper={continueBlocked && selectedOption !== undefined}
      />
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={confirm}
          disabled={continueBlocked}
          aria-describedby={continueBlocked ? helperId : undefined}
        >
          Continuar
        </Button>
      </DialogActions>
    </>
  );
};

export const ActivityJourneyLauncher = ({
  open,
  onClose,
}: ActivityJourneyLauncherProps) => {
  const titleId = useId();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
    >
      {open && (
        <ActivityJourneyDialogBody titleId={titleId} onClose={onClose} />
      )}
    </Dialog>
  );
};
